import _ from "lodash";
import React, { useState, useMemo } from "react";
import classes from "./CreateCalendarDeadlineModal.scss";
import { connect } from "react-redux";
import {
  Button,
  PageHeader,
  Tabs,
  Dialog,
  EmptyState,
} from "@toddle-edu/ds-web";

import UIModal from "UIComponents/UIModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import {
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
  getOrganizationId,
} from "modules/CommonSelectors";
import { useI18n } from "Hooks";
import {
  getTagList,
  getPersonalProjectGroupId,
  getCommunityProjectGroupId,
  getProjectGroups,
} from "MyCalendar/modules/Selector";
import { isStaffAdminForProjectModules } from "MyCalendar/modules/Utils";
import {
  getProjectGroupCategoryIdByType,
  getIsCpCoreEnabled,
  computeIsDeadlineAllDay,
} from "Projects/modules/Utils";
import DeadlineV2AddEditDetails from "Projects/routes/DeadlinesV2/components/DeadlinesV2AddEditDetails";
import {
  createDeadlineV2,
  updateDeadlineV2,
} from "Projects/routes/DeadlinesV2/modules/DeadlinesV2Modules";
import {
  DUPLICATE,
  CREATE,
  CURRICULUM_TYPE_MYP,
} from "Constants/stringConstants";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";

import { getStepListMap } from "./modules/Utils";
import MYPProjectYearGroupStep from "./components/MYPProjectYearGroupStep";
import { NoProjectIllustration } from "@toddle-edu/ds-theme";

const styles = {
  tabContainerStyle: {
    width: "100%",
    justifyContent: "flex-start",
  },
  deadlinesContainerStyle: {
    padding: 0,
  },
};

const CreateCalendarDeadlineModal = props => {
  const {
    currentCurriculumProgramType,
    onClose,
    tagList,
    projectGroups,
    mode = CREATE,
    eventDetailsProps = {},
    refetchEvents,
    createDeadlineV2,
    updateDeadlineV2,
    curriculumAcronym,
    overlayContainer,
    testId,
  } = props;

  const isCreateMode = mode === CREATE;
  const isDuplicateMode = mode === DUPLICATE;

  const { t } = useI18n(["common", "project"]);

  const [eventDetails, setEventDetails] = useState(eventDetailsProps);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(
    false
  );
  const [validationErrors, setValidationErrors] = useState({
    hasClosesOnError: false,
  });
  const [showLoader, setShowLoader] = useState(false);
  const tabOptions = getStepListMap({
    curriculumProgram: currentCurriculumProgramType,
    t,
  });

  const [selectedTab, setSelectedTab] = useState(
    isCreateMode ? _.first(tabOptions)?.value : _.last(tabOptions)?.value
  );

  const { tag } = eventDetails;
  const isIAEATag = tag === MY_CALENDAR_CONSTANTS.INTERNAL_EXTERNAL_ASSESSMENT;

  const isSelectedTabLastTab = _.isEqual(
    selectedTab,
    _.last(tabOptions)?.value
  );

  const isSelectedTabFirstTab = _.isEqual(
    selectedTab,
    _.first(tabOptions)?.value
  );

  const { buttonTwoText, isNextStepDisabled, pageHeaderTitle } = useMemo(() => {
    let buttonTwoText = t("common:next");
    let isNextStepDisabled = false;
    const isMYPCurriculum =
      currentCurriculumProgramType === CURRICULUM_TYPE_MYP;
    let pageHeaderTitle = "";
    if (isCreateMode) {
      pageHeaderTitle = isMYPCurriculum
        ? t("project:create_myp_deadline")
        : getIsCpCoreEnabled()
        ? t("project:create_label_deadline", {
            label: curriculumAcronym,
          })
        : t("project:create_dp_deadline");
    } else if (isDuplicateMode) {
      pageHeaderTitle = isMYPCurriculum
        ? t("project:duplicate_myp_deadline")
        : t("project:duplicate_dp_deadline");
    } else {
      pageHeaderTitle = isMYPCurriculum
        ? t("project:edit_myp_deadline")
        : t("project:edit_dp_deadline");
    }

    switch (selectedTab) {
      case "myp_project_year_group_select":
        if (!eventDetails?.yearGroup) {
          isNextStepDisabled = true;
        }
        break;
      case "dp_project_year_group_select":
        {
          if (isIAEATag) {
            if (!eventDetails?.component) {
              isNextStepDisabled = true;
            }
          } else {
            if (!eventDetails?.yearGroup) {
              isNextStepDisabled = true;
            }
          }
        }
        break;
      case "deadline_details":
        if (!eventDetails?.title || validationErrors?.hasClosesOnError) {
          isNextStepDisabled = true;
        }
        break;
    }

    if (isSelectedTabLastTab) {
      if (isCreateMode) {
        buttonTwoText = t("common:create");
      } else if (isDuplicateMode) {
        buttonTwoText = t("common:duplicate");
      } else {
        buttonTwoText = t("common:update");
      }
    } else {
      buttonTwoText = t("common:next");
    }
    return { buttonTwoText, isNextStepDisabled, pageHeaderTitle };
  }, [
    isCreateMode,
    isDuplicateMode,
    isSelectedTabLastTab,
    eventDetails,
    currentCurriculumProgramType,
    isIAEATag,
    validationErrors?.hasClosesOnError,
  ]);

  const onTabChange = value => {
    if (isSelectedTabFirstTab && isNextStepDisabled) {
      return;
    }

    setSelectedTab(value);
  };

  const onCreateDeadlineClick = async () => {
    setShowLoader(true);

    const {
      dueOn: deadlineDueOn,
      closesOn: deadlineClosesOn,
      templateAttachments: deadlineTemplateAttachments,
      resources: deadlineResources,
      yearGroup: projectGroupId,
      component: componentId,
    } = eventDetails;

    const isDeadlineAllDay = computeIsDeadlineAllDay(deadlineDueOn);

    await createDeadlineV2({
      input: {
        projectGroupId: isIAEATag ? componentId : projectGroupId,
        ..._.omit(eventDetails, [
          "templateAttachments",
          "resources",
          "yearGroup",
          "tag",
          "resources",
          "organizationResources",
          "submission",
          "responses",
          "id",
          "subject",
          "component",
        ]),
        dueOn: deadlineDueOn ? deadlineDueOn.utc() : undefined,
        closesOn: deadlineClosesOn ? deadlineClosesOn.utc() : undefined,
        isDeadlineAllDay,
        templates: _.map(deadlineTemplateAttachments, item => {
          return {
            name: item.name,
            type: item?.type,
            url: item?.url,
            mimeType: item?.mimeType,
            metadata: item?.metadata,
          };
        }),
        resources: _.map(deadlineResources, resourceAttachment => {
          return {
            label: resourceAttachment?.name,
            resourceType: "FILE",
            type: "PROJECT_GROUP_TASK_RESOURCE",
            attachment: {
              name: resourceAttachment?.name,
              type: resourceAttachment?.type,
              url: resourceAttachment?.url,
              mimeType: resourceAttachment?.mimeType,
              metadata: resourceAttachment?.metadata,
            },
          };
        }),
      },
      refetchQueries: false,
    });
    await refetchEvents();

    setShowLoader(false);
    onClose();
  };

  // can we have a common function here, jatin

  const onUpdateDeadlineClick = async () => {
    setShowLoader(true);

    const {
      dueOn: deadlineDueOn,
      closesOn: deadlineClosesOn,
      templateAttachments: deadlineTemplateAttachments,
      resources: deadlineResources,
      yearGroup: projectGroupId,
      component: componentId,
    } = eventDetails;

    const isDeadlineAllDay = computeIsDeadlineAllDay(deadlineDueOn);

    const addedAttachments = _.differenceBy(
      deadlineTemplateAttachments,
      eventDetailsProps?.templateAttachments,
      "id"
    );
    const removedAttachments = _.map(
      _.differenceBy(
        eventDetailsProps?.templateAttachments,
        deadlineTemplateAttachments,
        "id"
      ),
      item => item?.id
    );

    // get the attachments whose title has been changed

    const updatedAttachments = _.map(
      _.filter(eventDetailsProps?.templateAttachments, attachmentInDetails => {
        const attachment = _.find(deadlineTemplateAttachments, {
          id: attachmentInDetails?.id,
        });
        return attachment?.name !== attachmentInDetails?.name;
      }),
      attachmentInDetails => {
        const attachment = _.find(deadlineTemplateAttachments, {
          id: attachmentInDetails?.id,
        });
        return {
          id: attachmentInDetails?.id,
          name: attachment?.name,
        };
      }
    );

    const addedResources = _.differenceBy(
      deadlineResources,
      eventDetailsProps?.resources,
      "id"
    );
    const addedOrgResources = _.map(addedResources, resourceAttachment => {
      return {
        label: resourceAttachment?.name,
        resourceType: "FILE",
        type: "PROJECT_GROUP_TASK_RESOURCE",
        attachment: resourceAttachment,
      };
    });

    const removedResources = _.differenceBy(
      eventDetailsProps?.resources,
      deadlineResources,
      "id"
    );
    const removedOrgResources = _.map(
      _.filter(eventDetailsProps?.organizationResources, item => {
        return _.find(removedResources, { id: item?.attachment?.id });
      }),
      item => item?.id
    );

    const updatedResources = _.map(
      _.filter(eventDetailsProps?.resources, resourceInDetails => {
        const resourceAttachment = _.find(deadlineResources, {
          id: resourceInDetails?.id,
        });
        return resourceAttachment?.name !== resourceInDetails?.name;
      }),
      resourceInDetails => {
        const resourceAttachment = _.find(deadlineResources, {
          id: resourceInDetails?.id,
        });
        return {
          id: resourceInDetails?.id,
          name: resourceAttachment?.name,
        };
      }
    );

    await updateDeadlineV2({
      input: {
        ..._.omit(eventDetails, [
          "templateAttachments",
          "resources",
          "organizationResources",
          "submission",
          "responses",
          "tag",
          "yearGroup",
        ]),
        dueOn:
          deadlineDueOn && deadlineDueOn.isValid()
            ? deadlineDueOn.utc()
            : deadlineDueOn,
        closesOn:
          deadlineClosesOn && deadlineClosesOn.isValid()
            ? deadlineClosesOn?.utc()
            : deadlineClosesOn,
        templates: {
          added: addedAttachments,
          removed: removedAttachments,
          updated: [...updatedAttachments],
        },
        resources: {
          added: addedOrgResources,
          removed: removedOrgResources,
          updatedAttachments: updatedResources,
        },
        isDeadlineAllDay,
      },
      projectGroupId: isIAEATag ? componentId : projectGroupId,
      sourceType: "calendar",
    });

    await refetchEvents();
    setShowLoader(false);

    onClose();
  };

  const onUpdateEventDetails = ({ key, value }) => {
    if (
      key === "dueOn" &&
      (value == null ||
        (eventDetails?.closesOn && value.isAfter(eventDetails?.closesOn)))
    ) {
      {
        setEventDetails(prevState => ({
          ...prevState,
          ["closesOn"]: null,
        }));
      }
    }

    if (key === "tag" && value !== eventDetails.tag) {
      setEventDetails(prevState => ({
        ...prevState,
        ["tag"]: value,
        ["yearGroup"]: null,
        ["subject"]: null,
        ["component"]: null,
      }));
    }

    setEventDetails(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleValidationChange = errors => {
    setValidationErrors(prevErrors => ({
      ...prevErrors,
      ...errors,
    }));
  };

  const getBodyContent = () => {
    switch (selectedTab) {
      case "myp_project_year_group_select":
      case "dp_project_year_group_select":
        return (
          <MYPProjectYearGroupStep
            onUpdateEventDetails={onUpdateEventDetails}
            eventDetails={eventDetails}
            tagList={tagList}
            projectGroups={projectGroups}
            disabled={!isCreateMode}
            testId={`${testId}-yearGroupStep`}
          />
        );
      case "deadline_details":
        return (
          <DeadlineV2AddEditDetails
            deadlineDetails={eventDetails}
            containerStyle={styles.deadlinesContainerStyle}
            onUpdateDeadline={onUpdateEventDetails}
            onValidationChange={handleValidationChange}
            testId={`${testId}-deadlineDetailsStep`}
          />
        );
      default:
        return null;
    }
  };

  const onCloseDialog = () => {
    setShowUnsavedChangesDialog(false);
  };

  const handleModalClose = () => {
    if (!_.isEqual(eventDetails, eventDetailsProps)) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  };

  const onNextClick = () => {
    const selectedIndex = _.findIndex(tabOptions, { value: selectedTab });
    setSelectedTab(tabOptions[selectedIndex + 1]?.value);
  };

  const onPrevClick = () => {
    const selectedIndex = _.findIndex(tabOptions, { value: selectedTab });
    setSelectedTab(tabOptions[selectedIndex - 1]?.value);
  };

  return (
    <UIModal
      isFullScreen={true}
      isOpen={true}
      forceHideGlobalHeader={true}
      overlayContainer={overlayContainer}
    >
      <div className={classes.container}>
        <div className={classes.pageHeaderContainer}>
          <PageHeader>
            <PageHeader.Left
              title={pageHeaderTitle}
              // icon={<CalendarScheduleOutlined size="xx-small" />}
              onBackClick={handleModalClose}
            />
          </PageHeader>
        </div>
        {_.size(tagList) > 0 ? (
          <>
            <div>
              <Tabs
                options={tabOptions}
                value={selectedTab}
                onChange={onTabChange}
                variant={"container"}
                containerStyle={styles.tabContainerStyle}
                type="fixed"
              />
            </div>
            <div className={classes.bodyContainer}>
              <div className={classes.bodyInnerContainer}>
                {getBodyContent()}
              </div>
            </div>
            <div className={classes.footerContainer}>
              <div className={classes.footerInnerContainer}>
                <Button
                  variant="neutral"
                  type="outlined"
                  onClick={
                    isSelectedTabFirstTab ? handleModalClose : onPrevClick
                  }
                >
                  {isSelectedTabFirstTab
                    ? t("common:discard")
                    : t("common:back")}
                </Button>
                <Button
                  disabled={isNextStepDisabled}
                  variant={isSelectedTabLastTab ? "primary" : "progressive"}
                  type="fill"
                  onClick={
                    isSelectedTabLastTab
                      ? isCreateMode || isDuplicateMode
                        ? onCreateDeadlineClick
                        : onUpdateDeadlineClick
                      : onNextClick
                  }
                  testId={`${testId}-proceedButton`}
                >
                  {buttonTwoText}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            illustration={NoProjectIllustration}
            title={t("project:no_projects_enabled_calendar_empty_text")}
            dsVersion="2.0"
          />
        )}
      </div>
      {showLoader && <FullScreenLoader />}

      {showUnsavedChangesDialog && (
        <Dialog
          onClose={onCloseDialog}
          title={t("common:discard_changes")}
          message={t("common:dicard_the_changes_warning_msg")}
          buttonOneText={t("common:agree_cancel")}
          buttonTwoText={t("common:action_agree", {
            action: t("common:discard"),
          })}
          onButtonOneClick={onCloseDialog}
          onButtonTwoClick={onClose}
        />
      )}
    </UIModal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { eventType, t } = ownProps;
  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);

  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const organizationId = getOrganizationId(state);

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  const isStaffProjectAdmin = isStaffAdminForProjectModules({
    projectGroupCategoryIdByType,
    curriculumProgramId: currentCurriculumProgramId,
  });

  const projectGroups = getProjectGroups(state, isStaffProjectAdmin);
  const personalProjectGroupId = getPersonalProjectGroupId(state);
  const communityProjectGroupId = getCommunityProjectGroupId(state);

  const curriculumAcronym = _.get(
    state,
    "platform.currentCurriculumProgram.acronym"
  );

  const tagList = getTagList(
    state,
    eventType,
    currentCurriculumProgramType,
    projectGroups,
    personalProjectGroupId,
    communityProjectGroupId,
    t
  );

  return {
    currentCurriculumProgramType,
    projectGroups,
    tagList,
    curriculumAcronym,
  };
};

const mapActionCreators = {
  createDeadlineV2,
  updateDeadlineV2,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(CreateCalendarDeadlineModal);
